import { Pathname } from "./pathname";


export const PathnameGenerator = (name: string) => {
	return Pathname.DASHBOARD_HRMS + name;
};

const CRUD_ACTIONS = { ADD: 'add', VIEW: 'view', EDIT: 'edit' } as const;
type ModulePaths = Record<string, string>;
const COMPANY_MASTER_POLICY = '/company-master-policy';
export const MAIN_MENU: ModulePaths = {
	COMPANY_MASTER_POLICY: "company-master-policy",
	TIME_TRACKING: "time-tracking",
	RECRUITMENT: "recruitment",
	EMPLOYEE_MANAGEMENT: "employee-management",
	ONBOARDING: "onboarding",
	LEAVES: "leaves",
	RESOURCE_MANAGEMENT: "resource-management",
	PAYROLL_MANAGEMENT: "payroll-management",
	ORGANISATION: "organisation",
	ASSET_MANAGEMENT: "asset-management",
	LOAN_MANAGEMENT: "loan-management",
};

export const MODULES: ModulePaths = {
	// General
	APPROVAL_WORKFLOW: 'approval-workflow',
	APPROVAL_DASHBOARD: 'approval-dashboard',
	ORGANIZATION_STRUCTURE: 'organization-structure',
	RESIGNATION_TERMINATION: 'resignation-termination',

	// Company Master Policy
	LOAN_CONFIGURATION: 'loan-configuration',
	ACCRUALS_AND_BENEFIT: 'accruals-and-benefit',
	DOCUMENT_MASTER: 'document-master',
	DEPARTMENT_MASTER: 'department-master',
	DESIGNATION_MASTER: 'designation-master',
	COMPANY_CALENDAR: 'company-calendar',
	LEAVE_POLICY_MASTER: 'leave-policy-master',
	SALARY_STRUCTURE_MASTER: 'salary-structure-master',

	// Employee Management
	EMPLOYEE_MANAGEMENT: 'employee-management',
	EMPLOYEE_MASTER: 'employee-master',
	EMPLOYEE_CONTRACTS: 'employee-contracts',
	EMPLOYEE_ASSETS: 'employee-assets',

	// Recruitment
	HIRE_REQUEST: 'hire-request',
	REQUISITION_MANAGEMENT: 'requisition-management',
	CANDIDATE_MANAGEMENT: 'candidate-management',
	JOB_POSTING: 'job-posting',

	// Onboarding
	OFFER_LETTER: 'offer-letter',

	// Asset Management
	ASSET_LIST: 'asset-list',
	ASSET_REQUEST: 'asset-request',
	ASSET_TRANSFER: 'asset-transfer',
	ASSET_ALLOCATION: 'asset-allocation',
	ASSET_TRACKER: 'asset-tracker',
	DAMAGE_LOSS_CLAIM: 'damage-loss-claim',
	ASSET_HISTORY: 'asset-history',

	// Leaves
	LEAVE_MANAGEMENT_EMPLOYEE: 'leave-management',
	LEAVE_REQUEST: 'leave-request',

	// Time Tracking
	TIME_SHEET: 'time-sheet',
	TIME_CARD: 'time-cards',
	TIME_CARD_ENTRY: 'time-card-entry',

	// Resource Management
	RESOURCE_ALLOCATION: 'resource-allocation',
	RESOURCE_ASSIGNMENT: 'resource-assignment',

	// Payroll Management
	MONTHLY_PAYROLL: 'monthly-payroll',
	WPS_GENERATION: 'wps-generation',
	PAYSLIPS: 'payslips',
	GENERATE_PAYSLIPS: 'generate-payslip',

	// Loan Management
	LOAN_REQUEST: 'loan-request',

	// Reports
	EMPLOYEE_REPORT: 'employee-report',
};
export const generateCrudPaths = (
	base: string,
	entity: string,
	prefix = '',
	keyPostfix = ''
) =>
	Object.fromEntries(
		Object.entries(CRUD_ACTIONS).map(([key, action]) => {
			const isEditOrView = action === 'edit' || action === 'view';

			const routeParts = [prefix, MODULES[base]];
			if (isEditOrView) {
				routeParts.push(':id', `${action}-${entity}`);
			} else {
				routeParts.push(`${action}-${entity}`);
			}

			return [
				`${key}_${keyPostfix.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase()}`,
				`/${routeParts.filter(Boolean).join('/')}`,
			];
		})
	);


export const generateModulePaths = (mainModule: keyof typeof MAIN_MENU, subModule: keyof typeof MODULES = '', entity: string, keyPostfix = '') => ({
	[subModule.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase()]: `/${[MAIN_MENU[mainModule], MODULES[subModule]].filter(Boolean).join('/')}`,
	...generateCrudPaths(subModule, entity, MAIN_MENU[mainModule], keyPostfix),
});


export const ROUTES = {
	// Misc
	FORMS: '/forms',
	CUSTOM_FORM: '/custom-form',

	// Company Master Policy
	EMPLOYEE_CONTRACT_MASTER: `${COMPANY_MASTER_POLICY}/employee-contract-master`,
	DOCUMENT_MASTER: `${COMPANY_MASTER_POLICY}/document-master`,
	ACCRUALS_BENEFITS_MASTER: `${COMPANY_MASTER_POLICY}/accruals-benefits-master`,
	SALARY_STRUCTURE_MASTER: `${COMPANY_MASTER_POLICY}/salary-structure-master`,
	LOAN_CONFIGURATION_MASTER: `${COMPANY_MASTER_POLICY}/loan-configuration-master`,
	COMPANY_CALENDAR: `${COMPANY_MASTER_POLICY}/company-calendar`,
	...generateModulePaths('COMPANY_MASTER_POLICY', 'APPROVAL_WORKFLOW', 'approval-workflow', 'APPROVAL_WORKFLOW'),

	// Approval Workflow (standalone)
	...generateModulePaths('', 'APPROVAL_WORKFLOW', 'approval-workflow', 'APPROVAL_WORKFLOW_STANDALONE'),

	// Document master
	...generateModulePaths('COMPANY_MASTER_POLICY', 'DOCUMENT_MASTER', 'document-master', 'DOCUMENT_MASTER'),

	// Department master
	...generateModulePaths('ORGANISATION', 'DEPARTMENT_MASTER', 'department-master', 'DEPARTMENT_MASTER'),

	// Designation master
	...generateModulePaths('ORGANISATION', 'DESIGNATION_MASTER', 'designation-master', 'DESIGNATION_MASTER'),

	// Accruals & benefit
	...generateModulePaths('COMPANY_MASTER_POLICY', 'ACCRUALS_AND_BENEFIT', 'accruals-and-benefit', 'ACCRUALS_AND_BENEFIT'),

	// Company Calendar
	...generateModulePaths('COMPANY_MASTER_POLICY', 'COMPANY_CALENDAR', 'company-calendar', 'COMPANY_CALENDAR'),

	// Leave Policy Master
	...generateModulePaths('COMPANY_MASTER_POLICY', 'LEAVE_POLICY_MASTER', 'leave-policy-master', 'LEAVE_POLICY_MASTER'),

	// Salary Structure Master
	...generateModulePaths('COMPANY_MASTER_POLICY', 'SALARY_STRUCTURE_MASTER', 'salary-structure-master', 'SALARY_STRUCTURE_MASTER'),

	// Loan Configuration (standalone)
	...generateModulePaths('', 'LOAN_CONFIGURATION', 'loan-configuration', 'LOAN_CONFIGURATION'),

	// Organization Structure
	ORGANIZATION_STRUCTURE: '/organization-structure',
	...generateModulePaths('', 'ORGANIZATION_STRUCTURE', 'organization-structure', 'ORGANIZATION_STRUCTURE'),

	// Approval Dashboard
	APPROVAL_DASHBOARD: '/approval-dashboard',
	VIEW_APPROVAL_DASHBOARD: '/approval-dashboard/:id/view-approval-dashboard',

	// Employee Management
	EMPLOYEE_MANAGEMENT: '/employee-management',
	...generateModulePaths('EMPLOYEE_MANAGEMENT', 'EMPLOYEE_MASTER', 'employee', 'EMPLOYEE_MASTER'),
	...generateModulePaths('EMPLOYEE_MANAGEMENT', 'EMPLOYEE_CONTRACTS', 'employee-contracts', 'EMPLOYEE_CONTRACTS'),
	...generateModulePaths('EMPLOYEE_MANAGEMENT', 'EMPLOYEE_ASSETS', 'asset', 'EMPLOYEE_ASSETS'),

	// Asset Management Pages
	...generateModulePaths('ASSET_MANAGEMENT', 'ASSET_LIST', 'asset-list', 'ASSET_LIST'),
	...generateModulePaths('ASSET_MANAGEMENT', 'ASSET_REQUEST', 'asset-request', 'ASSET_REQUEST'),
	...generateModulePaths('ASSET_MANAGEMENT', 'ASSET_TRANSFER', 'asset-transfer', 'ASSET_TRANSFER'),
	...generateModulePaths('ASSET_MANAGEMENT', 'ASSET_ALLOCATION', 'asset-allocation', 'ASSET_ALLOCATION'),
	...generateModulePaths('ASSET_MANAGEMENT', 'ASSET_TRACKER', 'asset-tracker', 'ASSET_TRACKER'),
	...generateModulePaths('ASSET_MANAGEMENT', 'DAMAGE_LOSS_CLAIM', 'damage-loss-claim', 'DAMAGE_LOSS_CLAIM'),
	ASSET_RETURN: '/asset-return',

	// Time Tracking
	...generateModulePaths('TIME_TRACKING', 'TIME_CARD', 'time-card', 'TIME_CARD'),
	...generateModulePaths('TIME_TRACKING', 'TIME_SHEET', 'time-sheet', 'TIME_SHEET'),

	// Recruitment
	RECRUITMENT: '/recruitment',
	TALENT_POOL_DASHBOARD: '/recruitment/talent-pool-dashboard',
	...generateModulePaths('RECRUITMENT', 'HIRE_REQUEST', 'hire-request', 'HIRE_REQUEST'),
	...generateModulePaths('RECRUITMENT', 'REQUISITION_MANAGEMENT', 'requisition-management', 'REQUISITION_MANAGEMENT'),
	...generateModulePaths('RECRUITMENT', 'JOB_POSTING', 'job-posting', 'JOB_POSTING'),
	...generateModulePaths('RECRUITMENT', 'CANDIDATE_MANAGEMENT', 'candidate', 'CANDIDATE_MANAGEMENT'),

	// Onboarding
	...generateModulePaths('ONBOARDING', 'OFFER_LETTER', 'offer-letter', 'OFFER_LETTER'),
	ONBOARDING_MANAGEMENT: '/onboarding-management',

	// Leaves
	...generateModulePaths('LEAVES', 'LEAVE_MANAGEMENT_EMPLOYEE', 'leave-management', 'LEAVE_MANAGEMENT_EMPLOYEE'),
	...generateModulePaths('LEAVES', 'LEAVE_REQUEST', 'leave-request', 'LEAVE_REQUEST'),
	LEAVE_MANAGEMENT: '/leave-management',

	// Resignation & Termination
	RESIGNATION_LIST: '/resignations',
	TERMINATION_LIST: '/terminations',
	ADD_RESIGNATION: '/resignation-termination/add-resignation',
	EDIT_RESIGNATION: '/resignation-termination/:id/edit-resignation',
	VIEW_RESIGNATION: '/resignation-termination/:id/view-resignation',
	ADD_TERMINATION: '/resignation-termination/add-termination',
	EDIT_TERMINATION: '/resignation-termination/:id/edit-termination',
	VIEW_TERMINATION: '/resignation-termination/:id/view-termination',
	CLEARANCE_REVIEW: '/resignation-termination/:id/clearance-review',
	RESIGNATION_FINAL_SETTLEMENT: '/resignation-termination/:id/resignation-final-settlement',
	TERMINATION_FINAL_SETTLEMENT: '/resignation-termination/:id/termination-final-settlement',

	// Resource Management
	RESOURCE_ALLOCATION: '/resource-management/resource-allocation-requests',
	ADD_RESOURCE_ALLOCATION: '/resource-management/resource-allocation/add-resource-allocation',
	EDIT_RESOURCE_ALLOCATION: '/resource-management/resource-allocation/:id/edit-resource-allocation',
	VIEW_RESOURCE_REQUIREMENTS: '/resource-management/resource-allocation/:id/view-resource-requirements',
	RESOURCE_ASSIGNMENT: '/resource-management/resource-assignment',
	ADD_RESOURCE_ASSIGNMENT: '/resource-management/resource-assignment/add-resource-assignment',
	EDIT_RESOURCE_ASSIGNMENT: '/resource-management/resource-assignment/:id/edit-resource-assignment',
	VIEW_RESOURCE_ASSIGNMENT: '/resource-management/resource-assignment/:id/view-resource-assignment',

	// Payroll Management
	...generateModulePaths('PAYROLL_MANAGEMENT', 'MONTHLY_PAYROLL', 'monthly-payroll', 'MONTHLY_PAYROLL'),
	...generateModulePaths('PAYROLL_MANAGEMENT', 'WPS_GENERATION', 'wps-generation', 'WPS_GENERATION'),
	...generateModulePaths('PAYROLL_MANAGEMENT', 'PAYSLIPS', 'payslips', 'PAYSLIPS'),
	...generateModulePaths('PAYROLL_MANAGEMENT', 'GENERATE_PAYSLIPS', 'generate-payslip', 'GENERATE_PAYSLIPS'),

	// Loan Management
	LOAN_REQUEST_LIST: '/loan-requests',
	ADD_LOAN_REQUEST: '/loan-requests/add-loan',
	EDIT_LOAN_REQUEST: '/loan-requests/:id/edit-loan',
	VIEW_LOAN_REQUEST: '/loan-requests/:id/view-loan',

	// Reports
	...generateModulePaths('', 'EMPLOYEE_REPORT', 'employee-report', 'EMPLOYEE_REPORT'),
	EMPLOYEE_LIST_REPORT: '/employee-list-report',
	RECRUITMENT_REQUEST_REPORT: '/recruitment-request-report',
	RECRUITMENT_FULFILMENT_REPORT: '/recruitment-fulfilment-report',
	DEPARTMENT_ANALYSIS_REPORT: '/department-analysis-report',
	EMPLOYEE_SALARY_REPORT: '/employee-salary-report',
	CONTRACT_LIST_REPORT: '/contract-list-report',
	RESIGNATION_REQUEST_REPORT: '/resignation-request-report',
	ANNUAL_LEAVE_REPORT: '/annual-leave-report',
	EMPLOYEE_LOAN_REPORT: '/employee-loan-report',
	PENDING_LOAN_STATUS_REPORT: '/pending-loan-status-report',
	SALARY_SHEET_REPORT: '/salary-sheet-report',
	TIME_SHEET_REPORT: '/time-sheet-report',
	TIME_CARD_REPORT: '/time-card-report',
	MISSING_ATTENDANCE_REPORT: '/missing-attendance-report',
	EMPLOYEE_OVERTIME_REPORT: '/employee-overtime-report',
	LEAVE_ENCASHMENT_REPORT: '/leave-encashment-report',
	ASSET_STATUS_REPORT: '/asset-status-report',
	ASSET_DAMAGE_REPORT: '/asset-damage-report',
	EMPLOYEE_GRATUITY_REPORT: '/employee-gratuity-report',
	EMPLOYEE_TURNOVER_REPORT: '/employee-turnover-report',
	ATTENDANCE_SUMMARY_REPORT: '/attendance-summary-report',
	EMPLOYEE_PROBATION_REPORT: '/employee-probation-report',
	EXPIRING_DOCUMENTS_REPORT: '/expiring-documents-report',
	PAYROLL_VARIANCE_REPORT: '/payroll-variance-report',
	EXPENSE_REIMBURSEMENT_REPORT: '/expense-reimbursement-report',
	TERMINATION_REQUEST_REPORT: '/termination-request-report',
	LEAVE_REQUEST_REPORT: '/leave-request-report',
	EMPLOYEE_ASSET_RECORDS_REPORT: '/employee-asset-records-report',

	// Offboarding / Attendance (legacy stubs)
	OFFBOARDING_MANAGEMENT: '/offboarding-management',
	ATTENDANCE_TIME_TRACKING: '/attendance-time-tracking',
} as const;

type ApprovalWorkflowRoutes = {
	APPROVAL_WORKFLOW: string;
	ADD_APPROVAL_WORKFLOW: string;
	VIEW_APPROVAL_WORKFLOW: string;
	EDIT_APPROVAL_WORKFLOW: string;
};
type LoanConfigurationRoutes = {
	LOAN_CONFIGURATION: string;
	ADD_LOAN_CONFIGURATION: string;
	VIEW_LOAN_CONFIGURATION: string;
	EDIT_LOAN_CONFIGURATION: string;

};
type DocumentMasterRoutes = {
	DOCUMENT_MASTER: string;
	ADD_DOCUMENT_MASTER: string;
	VIEW_DOCUMENT_MASTER: string;
	EDIT_DOCUMENT_MASTER: string;
};

type CompanyCalenderRoutes = {
	COMPANY_CALENDAR: string;
	ADD_COMPANY_CALENDAR: string;
	EDIT_COMPANY_CALENDAR: string;
	VIEW_COMPANY_CALENDAR: string;
}

type LeavePolicyMasterRoutes = {
	LEAVE_POLICY_MASTER: string;
	ADD_LEAVE_POLICY_MASTER: string;
	EDIT_LEAVE_POLICY_MASTER: string;
	VIEW_LEAVE_POLICY_MASTER: string;
}

type SalaryStructureMasterRoutes = {
	SALARY_STRUCTURE_MASTER: string;
	ADD_SALARY_STRUCTURE_MASTER: string;
	EDIT_SALARY_STRUCTURE_MASTER: string;
	VIEW_SALARY_STRUCTURE_MASTER: string;
}

type EmployeeManagementRoutes = {
	EMPLOYEE_MANAGEMENT: string;
	ADD_EMPLOYEE_MANAGEMENT: string;
	EDIT_EMPLOYEE_MANAGEMENT: string;
	VIEW_EMPLOYEE_MANAGEMENT: string;
}

export const PathnameHrms: typeof ROUTES & { LANDING_PAGE: string; LIST: string; FORMS: string; CUSTOM_FORM: string } & ApprovalWorkflowRoutes & LoanConfigurationRoutes & DocumentMasterRoutes & CompanyCalenderRoutes & LeavePolicyMasterRoutes & SalaryStructureMasterRoutes & EmployeeManagementRoutes = {
	LANDING_PAGE: '/',
	LIST: '/list',
	FORMS: '/settings/forms',
	CUSTOM_FORM: '/settings/custom-form',
	...ROUTES
} as any;

